import { IndicatorSeries, type Bounding, type IndicatorTemplate, type IndicatorDrawParams } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';

// Fundamentals data point: quarterly EPS/revenue with computed YoY growth.
export interface LogEpsPoint {
  /** Unix seconds of the fiscal period end */
  time: number;
  /** Raw quarterly EPS (basic) */
  eps: number;
  /** Raw quarterly revenue */
  revenue: number | null;
  /** Quarter label: Q1, Q2, Q3, Q4 */
  month: string;
  /** Fiscal year */
  year: number;
  /** YoY EPS growth ratio (e.g. 0.5 = +50%). Null when no same-quarter prior year exists. */
  growth: number | null;
  /** sign(growth) * log10(1 + |growth|). Symmetric around 0. Null when growth is null. */
  logGrowth: number | null;
  /** YoY revenue growth ratio. Null when no same-quarter prior year exists. */
  revenueGrowth: number | null;
  /** sign(revenueGrowth) * log10(1 + |revenueGrowth|). Null when revenueGrowth is null. */
  logRevenueGrowth: number | null;
}

// ── Drawing constants ─────────────────────────────────────────────────────────
const LINE_WIDTH        = 1.5;
const DOT_RADIUS        = 2.5;
const POINT_LABEL_FONT  = '9px sans-serif';
const POINT_LABEL_GAP   = 5;
const POINT_LABEL_PAD   = 2;
export const EPS_AXIS_WIDTH   = 56; // px reserved on the left for the secondary axis
const AXIS_WIDTH        = EPS_AXIS_WIDTH;
const AXIS_FONT         = 'normal 12px Helvetica Neue'; // matches klinecharts default axis tick font exactly

const EPS_COLOR_DARK   = '#22C55E';
const EPS_COLOR_LIGHT  = '#16A34A';
const LOSS_COLOR_DARK  = '#EF4444';
const LOSS_COLOR_LIGHT = '#DC2626';
const REVENUE_COLOR_DARK  = '#60A5FA';
const REVENUE_COLOR_LIGHT = '#2563EB';

function isLightTheme(): boolean {
  try { return useChartStore.getState().theme === 'light'; } catch { return false; }
}

export function resolveEpsColor(): string {
  return isLightTheme() ? EPS_COLOR_LIGHT : EPS_COLOR_DARK;
}

export function resolveLossColor(): string {
  return isLightTheme() ? LOSS_COLOR_LIGHT : LOSS_COLOR_DARK;
}

function resolveRevenueColor(): string {
  return isLightTheme() ? REVENUE_COLOR_LIGHT : REVENUE_COLOR_DARK;
}

function resolveLabelBgColor(): string {
  return isLightTheme() ? '#f0f3fa' : '#0f1115';
}

function resolveLabelBgAlpha(): number {
  return isLightTheme() ? 0.88 : 0.75;
}

function resolveAxisBgAlpha(): number {
  return isLightTheme() ? 0.82 : 0.78;
}

function resolveAxisBgColor(): string {
  return isLightTheme() ? '#e8edf5' : '#0b0e14';
}

function resolveAxisTextColor(): string {
  return isLightTheme() ? '#6b7280' : '#9ca3af';
}

function fmtGrowthPct(growth: number | null): string {
  if (growth == null) return '—';
  const pct = growth * 100;
  const capped = Math.max(-999, Math.min(999, pct));
  return `${capped >= 0 ? '+' : ''}${capped.toFixed(0)}%`;
}

function fmtEps(v: number): string {
  return v.toFixed(2);
}

type KBarPrice = { timestamp: number; close: number };

type DrawParams = IndicatorDrawParams<Record<string, never>> & {
  kLineDataList: KBarPrice[];
  ctx: CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  xAxis: { convertToPixel: (i: number) => number };
  yAxis: { convertToPixel: (v: number) => number };
  bounding: Bounding;
};

type TooltipParams = {
  kLineDataList: KBarPrice[];
  crosshair: { dataIndex?: number };
};

// kLineDataList is chronologically sorted, so the closest bar to a target
// timestamp can be found with a binary search instead of a full scan — this
// runs once per EPS point inside draw(), which klinecharts calls every frame
// during pan/zoom/crosshair-move, so an O(n) scan here is O(points × bars) per frame.
function findClosestBarIndex(dataList: KBarPrice[], targetUnixSec: number): number {
  if (dataList.length === 0) return -1;
  let lo = 0;
  let hi = dataList.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const midSec = Math.floor(dataList[mid].timestamp / 1000);
    if (midSec < targetUnixSec) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0) {
    const loSec = Math.floor(dataList[lo].timestamp / 1000);
    const prevSec = Math.floor(dataList[lo - 1].timestamp / 1000);
    if (Math.abs(prevSec - targetUnixSec) <= Math.abs(loSec - targetUnixSec)) return lo - 1;
  }
  return lo;
}

// ── Secondary axis math ───────────────────────────────────────────────────────

function symLog(v: number): number {
  return Math.sign(v) * Math.log10(1 + Math.abs(v));
}

function makeEpsToY(
  scale: 'linear' | 'log',
  paddedMin: number,
  paddedMax: number,
  top: number,
  height: number,
): (eps: number) => number {
  if (scale === 'log') {
    const logMin = symLog(paddedMin);
    const logMax = symLog(paddedMax);
    const range = logMax - logMin || 1;
    return (eps) => top + height * (1 - (symLog(eps) - logMin) / range);
  }
  const range = paddedMax - paddedMin || 1;
  return (eps) => top + height * (1 - (eps - paddedMin) / range);
}

function niceTickValues(min: number, max: number, pixelHeight: number): number[] {
  const minSpacingPx = 28; // minimum px between ticks
  const maxCount = Math.max(2, Math.floor(pixelHeight / minSpacingPx));
  const range = max - min || 1;

  // Candidate step sizes (nice round numbers)
  const rawStep = range / maxCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep))));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1 * magnitude;
  else if (normalized <= 2) niceStep = 2 * magnitude;
  else if (normalized <= 2.5) niceStep = 2.5 * magnitude;
  else if (normalized <= 5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  // Snap start to a multiple of niceStep
  const start = Math.ceil(min / niceStep) * niceStep;
  const ticks: number[] = [];
  // Round to avoid floating point drift
  const decimals = Math.max(0, -Math.floor(Math.log10(niceStep)));
  for (let v = start; v <= max + niceStep * 0.01; v += niceStep) {
    const rounded = parseFloat(v.toFixed(decimals + 1));
    if (rounded >= min - niceStep * 0.01 && rounded <= max + niceStep * 0.01) {
      ticks.push(rounded);
    }
  }
  return ticks;
}

// ── Indicator template ────────────────────────────────────────────────────────
const epsLine: IndicatorTemplate<Record<string, never>> = {
  name: 'EPS_LINE',
  shortName: 'EPS',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,
  figures: [],

  calc: (dataList) => dataList.map(() => ({})),

  createTooltipDataSource: (params: unknown) => {
    const p = params as TooltipParams;
    const data = window.__eps_line_data;
    if (!data?.points?.length) return { name: '', calcParamsText: '', icons: [], values: [] };
    const dataIndex = p.crosshair?.dataIndex ?? -1;
    const bar = dataIndex >= 0 ? p.kLineDataList[dataIndex] : null;
    if (!bar) return { name: '', calcParamsText: '', icons: [], values: [] };
    const barSec = Math.floor(bar.timestamp / 1000);
    let closest: LogEpsPoint | null = null;
    let minDiff = Infinity;
    for (const pt of data.points) {
      const diff = Math.abs(pt.time - barSec);
      if (diff < minDiff) { minDiff = diff; closest = pt; }
    }
    if (!closest || minDiff > 45 * 86400) return { name: '', calcParamsText: '', icons: [], values: [] };
    return {
      name: 'EPS',
      calcParamsText: '',
      icons: [],
      values: [
        { title: 'EPS: ', value: closest.eps.toFixed(2) },
        { title: 'YoY: ', value: fmtGrowthPct(closest.growth) },
        { title: 'Rev: ', value: fmtGrowthPct(closest.revenueGrowth) },
      ],
    };
  },

  draw: (params: unknown): boolean => {
    try {
      const { ctx, kLineDataList, visibleRange, xAxis, bounding } =
        params as DrawParams;

      const data = window.__eps_line_data;
      if (!data?.points?.length) return false;

      const { from, to } = visibleRange;
      if (from < 0 || to < 0 || from >= kLineDataList.length) return false;

      const { top, left, height } = bounding;

      // ── Pass 1: map all EPS points to bar indices and x ──────────────────
      type RawPt = {
        barIdx: number; x: number; eps: number; time: number;
        growth: number | null; revenueGrowth: number | null;
        month: string; year: number;
      };
      const rawPoints: RawPt[] = [];

      for (const pt of data.points) {
        const barIdx = findClosestBarIndex(kLineDataList, pt.time);
        if (barIdx < 0) continue;
        let x = xAxis.convertToPixel(barIdx);
        if (x == null || Number.isNaN(x)) {
          const fx = xAxis.convertToPixel(from);
          const lx = xAxis.convertToPixel(Math.min(to, kLineDataList.length - 1));
          if (fx != null && lx != null && !Number.isNaN(fx) && !Number.isNaN(lx) && to > from) {
            x = fx + ((barIdx - from) / (to - from)) * (lx - fx);
          } else continue;
        }
        rawPoints.push({ barIdx, x, eps: pt.eps, time: pt.time, growth: pt.growth, revenueGrowth: pt.revenueGrowth, month: pt.month, year: pt.year });
      }
      rawPoints.sort((a, b) => a.time - b.time);

      // Handle projected next-quarter point
      const proj = data.nextQuarterEps;
      let projPt: RawPt | null = null;
      if (proj?.value != null && proj.time != null) {
        const barIdx = findClosestBarIndex(kLineDataList, proj.time);
        if (barIdx >= 0) {
          let x = xAxis.convertToPixel(barIdx);
          if (x == null || Number.isNaN(x)) {
            const fx = xAxis.convertToPixel(from);
            const lx = xAxis.convertToPixel(Math.min(to, kLineDataList.length - 1));
            if (fx != null && lx != null && !Number.isNaN(fx) && !Number.isNaN(lx) && to > from) {
              x = fx + ((barIdx - from) / (to - from)) * (lx - fx);
            }
          }
          if (x != null && !Number.isNaN(x)) {
            const nq = data.nextQuarterGrowth;
            projPt = {
              barIdx, x, eps: proj.value, time: proj.time,
              growth: nq?.growthRate ?? null,
              revenueGrowth: null,
              month: '', year: 0,
            };
          }
        }
      }

      // ── Compute visible EPS range ─────────────────────────────────────────
      const visiblePts = rawPoints.filter((p) => p.barIdx >= from && p.barIdx <= to);
      const allForRange = [...visiblePts];
      if (projPt && projPt.barIdx >= from && projPt.barIdx <= to) allForRange.push(projPt);

      if (allForRange.length === 0 && rawPoints.length === 0) return false;

      const rangePts = allForRange.length > 0 ? allForRange : rawPoints;
      let minEps = rangePts[0].eps;
      let maxEps = rangePts[0].eps;
      for (const p of rangePts) {
        if (p.eps < minEps) minEps = p.eps;
        if (p.eps > maxEps) maxEps = p.eps;
      }
      let pad = (maxEps - minEps) * 0.18;
      if (pad < 0.5) pad = Math.max(0.5, Math.abs(maxEps) * 0.35 || 0.5);
      // Apply zoom: zoom > 1 narrows the visible range (zooming in), < 1 widens it
      // Apply pan: shifts the center up/down in EPS units
      const zoom = window.__eps_line_axis_zoom ?? 1;
      const pan  = window.__eps_line_axis_pan  ?? 0;
      const mid = (minEps + maxEps) / 2 + pan;
      const halfSpan = (pad + (maxEps - minEps) / 2) / zoom;
      const paddedMin = mid - halfSpan;
      const paddedMax = mid + halfSpan;

      // ── Resolve scale preference ──────────────────────────────────────────
      const axisScale = useChartStore.getState().epsLineAxisScale;
      const epsToY = makeEpsToY(axisScale, paddedMin, paddedMax, top, height);

      // ── Resolve colors ────────────────────────────────────────────────────
      const mainColor  = resolveEpsColor();
      const lossColor  = resolveLossColor();
      const revColor   = resolveRevenueColor();
      const axisBg     = resolveAxisBgColor();
      const axisText   = resolveAxisTextColor();

      ctx.save();

      // ── Draw secondary axis (left side) ───────────────────────────────────
      const axisX = left;
      const axisRight = axisX + AXIS_WIDTH;

      // Publish axis canvas bounds for wheel hit-testing in ChartContainer
      window.__eps_axis_bounds = { left: axisX, right: axisRight, top, bottom: top + height };

      // Background
      ctx.globalAlpha = resolveAxisBgAlpha();
      ctx.fillStyle = axisBg;
      ctx.fillRect(axisX, top, AXIS_WIDTH, height);
      ctx.globalAlpha = 1;

      // Right border
      ctx.strokeStyle = isLightTheme() ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.10)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(axisRight, top);
      ctx.lineTo(axisRight, top + height);
      ctx.stroke();

      // Ticks and labels
      ctx.font = AXIS_FONT;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = axisText;

      const tickValues = niceTickValues(paddedMin, paddedMax, height);
      const labelHalfH = 6; // half of 12px font height — guard edges
      for (const tv of tickValues) {
        const ty = epsToY(tv);
        if (ty < top + labelHalfH || ty > top + height - labelHalfH) continue;
        // tick line
        ctx.strokeStyle = isLightTheme() ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.10)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(axisRight - 4, ty);
        ctx.lineTo(axisRight, ty);
        ctx.stroke();
        // label
        ctx.fillStyle = axisText;
        ctx.fillText(fmtEps(tv), axisRight - 6, ty);
      }

      // Scale badge ("LOG" / "LIN") at bottom-left of axis
      const badge = axisScale === 'log' ? 'LOG' : 'LIN';
      ctx.font = '7px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = axisText;
      ctx.fillText(badge, axisX + 3, top + height - 3);

      // EPS label header
      ctx.font = '8px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = mainColor;
      ctx.fillText('EPS', axisX + AXIS_WIDTH / 2, top + 3);

      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // ── Draw positive EPS lines ───────────────────────────────────────────
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // ── Draw connecting lines — one segment per pair, color by destination ──
      // pos→pos: green solid | neg→neg: red dashed | pos→neg: red dashed | neg→pos: green solid
      for (let i = 0; i < rawPoints.length - 1; i++) {
        const curr = rawPoints[i];
        const next = rawPoints[i + 1];
        if ((next.time - curr.time) / 86400 > 180) continue;

        const goingNegative = next.eps <= 0;
        ctx.strokeStyle = goingNegative ? lossColor : mainColor;
        ctx.lineWidth = LINE_WIDTH;
        ctx.globalAlpha = goingNegative ? 0.75 : 1;
        ctx.setLineDash(goingNegative ? [3, 4] : []);

        ctx.beginPath();
        ctx.moveTo(curr.x, epsToY(curr.eps));
        ctx.lineTo(next.x, epsToY(next.eps));
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // Dashed projected line from last actual point to projection
      if (projPt && rawPoints.length > 0) {
        const last = rawPoints[rawPoints.length - 1];
        const goingNegative = projPt.eps <= 0;
        ctx.beginPath();
        ctx.setLineDash([3, 4]);
        ctx.strokeStyle = goingNegative ? lossColor : mainColor;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = LINE_WIDTH;
        ctx.moveTo(last.x, epsToY(last.eps));
        ctx.lineTo(projPt.x, epsToY(projPt.eps));
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      }

      // ── Dots ─────────────────────────────────────────────────────────────
      for (const pt of rawPoints) {
        ctx.fillStyle = pt.eps > 0 ? mainColor : lossColor;
        ctx.beginPath();
        ctx.arc(pt.x, epsToY(pt.eps), pt.eps > 0 ? DOT_RADIUS : DOT_RADIUS + 0.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Projected dot (hollow ring style)
      if (projPt) {
        const c = projPt.eps > 0 ? mainColor : lossColor;
        ctx.globalAlpha = 0.6;
        ctx.strokeStyle = c;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(projPt.x, epsToY(projPt.eps), DOT_RADIUS + 1, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // ── Per-point growth labels ───────────────────────────────────────────
      ctx.font = POINT_LABEL_FONT;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      const allLabelPts: (RawPt & { isProjection?: boolean })[] = [...rawPoints];
      if (projPt) allLabelPts.push({ ...projPt, isProjection: true });

      for (const pt of allLabelPts) {
        const lines: { text: string; color: string }[] = [];

        if (pt.growth != null) {
          const c = pt.growth < 0 ? lossColor : mainColor;
          lines.push({ text: `EPS:${fmtGrowthPct(pt.growth)}`, color: c });
        }
        if (!pt.isProjection && pt.revenueGrowth != null) {
          const c = pt.revenueGrowth < 0 ? lossColor : revColor;
          lines.push({ text: `Rev:${fmtGrowthPct(pt.revenueGrowth)}`, color: c });
        }
        if (lines.length === 0) continue;

        let maxW = 0;
        for (const ln of lines) { const w = ctx.measureText(ln.text).width; if (w > maxW) maxW = w; }
        const lineH = 10;
        const boxW = maxW + POINT_LABEL_PAD * 2;
        const boxH = lines.length * lineH + POINT_LABEL_PAD * 2;
        const dotY = epsToY(pt.eps);
        const boxX = pt.x - boxW / 2;
        const boxY = dotY + DOT_RADIUS + POINT_LABEL_GAP;

        ctx.globalAlpha = resolveLabelBgAlpha() * (pt.isProjection ? 0.7 : 1);
        ctx.fillStyle = resolveLabelBgColor();
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxW, boxH, 2);
        ctx.fill();
        ctx.globalAlpha = pt.isProjection ? 0.6 : 1;

        for (let i = 0; i < lines.length; i++) {
          ctx.fillStyle = lines[i].color;
          ctx.fillText(lines[i].text, pt.x, boxY + POINT_LABEL_PAD + i * lineH);
        }
        ctx.globalAlpha = 1;
      }

      ctx.restore();
      return false;
    } catch (e) {
      console.error('[EpsLine] draw error:', e);
      return false;
    }
  },
};

export default epsLine;
